import { BaseTextField } from '@components/BaseTextField';
import PopUpBase from '@components/PopUpBase';
import BaseEnhancedTabs, { TabConfig } from '@components/tabs/BaseEnhancedTabs';
import { pushSuccess } from '@components/toast';
import { cameraPhotoController, potentialGroupContraventionController } from '@controllers/index';
import { IGetCameraPhotoProps } from '@EcamModel/controllers/ICameraPhotoHttpController';
import { IUpdateEvidencePhoto } from '@EcamModel/controllers/IGroupPotentialContraventionHttpController';
import {
    CameraPhotoVehicles,
    GroupPotentialContraventions,
    PhotoType,
    PotentialContraventionWithInfo,
} from '@EcamModel/model';
import { IPopUp } from '@hooks/usePopUp';
import {
    Box,
    Button,
    CircularProgress,
    Fade,
    FormControl,
    FormControlLabel,
    Grid,
    Grow,
    Radio,
    RadioGroup,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';
import NoDataCameras from '@pages/cameras/list/components/NoDataCameras';
import { useVerificationQueueMode } from '@pages/verification/hooks/VerificationProvider';
import color from '@theme/Colors';
import _, { debounce, orderBy } from 'lodash';
import moment, { Moment } from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { FiInfo, FiSearch } from 'react-icons/fi';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useBackdrop } from 'src/providers/BackdropProvider';
import { usePotentialContravention } from '../hooks/PotentialContraventionProvider';
import { CustomTextField } from './ModalChangePlate';
import { PhotoDetails } from './PopupChangeEvidence';
import ScrollableBoxCustom from './ScrollableBoxCustom';
import { getSecond } from 'src/helpers';

type Props = Omit<IPopUp, 'onConfirm'> & {
    onPreviewChange: (value: boolean) => void;
    onSetSelectedPhotos: (photo: CameraPhotoVehicles) => void;
};

export default function PopUpReplacePhoto(props: Props) {
    const { open, onPreviewChange, onSetSelectedPhotos, onClose } = props;
    const backdrop = useBackdrop();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { selectedPc, vrn, setGroupPc, setVrn, handleInputChange, handleUpdateChange } = usePotentialContravention();
    const { verificationQueueMode } = useVerificationQueueMode();
    const { OverviewPhotos: overviewPhotos } = selectedPc ?? ({} as PotentialContraventionWithInfo);

    const searchPlateRef = useRef<HTMLInputElement | null>(null);
    const searchBoxRef = useRef<HTMLDivElement>(null);

    const [selectedItemsLivePhotos, setSelectedItemsLivePhotos] = useState<CameraPhotoVehicles | null>(null);
    const [tab, setTab] = useState<'overview' | 'search'>('overview');
    const [inputSearchValue, setInputSearchValue] = useState<string>('');
    const [searchValue, setSearchValue] = useState<string>('');
    const [fromTime, setFromTime] = useState<moment.Moment | null>(null);
    const [toTime, setToTime] = useState<moment.Moment | null>(null);
    const [searchList, setSearchList] = useState<CameraPhotoVehicles[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchPage, setSearchPage] = useState(1);
    const [searchHasMore, setSearchHasMore] = useState(true);

    const selectedPlate = !_.isNil(selectedItemsLivePhotos?.Plate);

    const handleRadioChangeItemsLivePhotos = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!(overviewPhotos || searchList)) return;
        const allPhotos = (overviewPhotos || []).concat(searchList || []);
        const selectedItem = allPhotos.find((item) => item.Id === Number(event.target.value));
        if (!selectedItem) return;
        setSelectedItemsLivePhotos(selectedItem);
    };

    const handleChangePhotoFullLicense = async () => {
        if (vrn) {
            handleUpdateChange(vrn);
        }

        backdrop.setTrue();
        const updateEvidencePhoto: IUpdateEvidencePhoto = {
            PotentialContraventionId: selectedPc?.Id!,
            UnobstructedPlatePhotos: {
                CameraPhotoVehicleId: selectedItemsLivePhotos?.Id!,
                PotentialContraventionId: selectedPc?.Id!,
                PhotoType: PhotoType.UnobstructedPlate,
            },
        };
        await potentialGroupContraventionController
            .doUpdateEvidencePhoto(updateEvidencePhoto)
            .then(async (res) => {
                queryClient.invalidateQueries(['trailAuditEventLogPCN']);
                await potentialGroupContraventionController
                    .getGroupPotentialContravention(selectedPc?.GroupPotentialContraventionId!)
                    .then((res) => {
                        props.onClose?.();
                        pushSuccess('Save successfully');
                        const _res: GroupPotentialContraventions = {
                            ...res,
                            PotentialContraventions: orderBy(res.PotentialContraventions, (pc) => -pc.Step),
                        };
                        setGroupPc(_res);
                    })
                    .catch((err) => navigate(`/${verificationQueueMode}`));
            })
            .catch((err) => { })
            .finally(() => {
                backdrop.setFalse();
            });
    };

    const debouncedSetSearchValue = useRef(
        _.debounce((val: string) => {
            setSearchValue(val);
        }, 500)
    ).current;

    const tabList: TabConfig[] = [
        {
            index: 0,
            label: 'Overview',
            onClick: () => setTab('overview'),
        },
        {
            index: 1,
            label: 'Search by time',
            onClick: () => setTab('search'),
        },
    ];

    const startOfHour: Moment = moment.tz(selectedPc?.EntryAt,selectedPc?.Timezone ?? moment.tz.guess()).startOf('day');
    const endOfHour: Moment = moment.tz(selectedPc?.EntryAt,selectedPc?.Timezone ?? moment.tz.guess()).endOf('day');

    const getPlateImageBundlePhotos = async (page = 1, append = false) => {
        setSearchLoading(true);
        try {
            const filter: IGetCameraPhotoProps = {
                page,
                pageSize: 20,
                CameraId: selectedPc?.CameraId!,
                Plate: searchValue || undefined,
                FromDate: startOfHour.toDate(),
                ToDate: endOfHour.toDate(),
                FromTime: fromTime ? getSecond(fromTime) : undefined,
                ToTime: toTime ? getSecond(toTime) : undefined,
                sorts: ['-CaptureAt'],
            };
            const res = await cameraPhotoController.getCameraPhotoVehiclesByPlate(filter);
            setSearchList((prev) => (append ? [...prev, ...res.rows] : res.rows));
            setSearchHasMore((append ? searchList.length + res.rows.length : res.rows.length) < res.total);
        } catch (e) {
            setSearchList([]);
            setSearchHasMore(false);
        } finally {
            setSearchLoading(false);
        }
    };

    useEffect(() => {
        if (tab === 'search') {
            getPlateImageBundlePhotos(1, false);
        }
    }, [searchValue, fromTime, toTime, tab]);

    useEffect(() => {
        if (tab !== 'search') return;
        const handleScroll = () => {
            if (!searchBoxRef.current) return;
            const { scrollTop, scrollHeight, clientHeight } = searchBoxRef.current;
            if (scrollTop + clientHeight >= scrollHeight - 10 && !searchLoading && searchHasMore) {
                const nextPage = searchPage + 1;
                setSearchPage(nextPage);
                getPlateImageBundlePhotos(nextPage, true);
            }
        };
        const ref = searchBoxRef.current;
        if (ref) ref.addEventListener('scroll', handleScroll);
        return () => {
            if (ref) ref.removeEventListener('scroll', handleScroll);
        };
    }, [searchLoading, searchHasMore, searchPage, tab]);

    useEffect(() => {
        setSearchPage(1);
    }, [searchValue, fromTime, toTime, tab]);

    useEffect(() => {
        const plate = selectedPc?.Plate ?? '';
        setInputSearchValue(plate);
        setSearchValue(plate);
    }, [selectedPc?.Plate]);

    useEffect(() => {
        setVrn(selectedItemsLivePhotos?.Plate || '');
    }, [selectedItemsLivePhotos?.Plate]);

    return (
        <PopUpBase
            open={open}
            dialogProps={{
                fullWidth: true,
                maxWidth: 'lg',
                sx: {
                    '& .MuiDialog-paper': {
                        borderRadius: '10px',
                    },
                    '& .MuiPaper-root': {
                        overflowY: 'unset',
                    },
                    zIndex: (theme) => theme.zIndex.modal,
                },
            }}
            onClose={onClose}
            title={<Typography variant="h4">Choose a full license plate photo</Typography>}
            titleProps={{ '&.MuiDialogTitle-root': { paddingTop: '24px' } }}
            minWidthButton={150}
            desc={
                <Box>
                    <BaseEnhancedTabs
                        tabs={tabList}
                        value={tab === 'overview' ? 0 : 1}
                        onChange={(_, newValue) => tabList[newValue].onClick?.()}
                        sx={{ '& .MuiButtonBase-root': { background: color.grey100 } }}
                    />
                    <Stack width={'100%'} bgcolor={color.grey100} p={2} borderRadius={1}>
                        {selectedPlate && (
                            <Fade in={true} timeout={700}>
                                <CustomTextField
                                    value={vrn}
                                    onChange={handleInputChange}
                                    placeholder={'Enter Reg'}
                                    autoFocus
                                    disabled={false}
                                    inputProps={{
                                        maxLength: 20,
                                    }}
                                    sx={{ mb: 2 }}
                                />
                            </Fade>
                        )}
                        {tab === 'overview' ? (
                            <ScrollableBoxCustom>
                                <Grid container spacing={2}>
                                    {Array.isArray(overviewPhotos) && overviewPhotos.length > 0 ? (
                                        overviewPhotos?.map((item, index) => (
                                            <Grow in={true} key={index} timeout={Math.min(500 * index, 1000)}>
                                                <Grid item md={3} sm={6} xs={12}>
                                                    <Grow in={!!item} timeout={500}>
                                                        <Box height={'100%'}>
                                                            <Box
                                                                key={item.Id}
                                                                bgcolor={color.white}
                                                                p={1}
                                                                border={1}
                                                                borderColor={color.grey200}
                                                                borderRadius={1}
                                                                position={'relative'}
                                                            >
                                                                <FormControl>
                                                                    <RadioGroup
                                                                        row
                                                                        aria-labelledby="radio-buttons-group-label"
                                                                        name="radio-buttons-group"
                                                                        value={selectedItemsLivePhotos?.Id || ''}
                                                                        onChange={handleRadioChangeItemsLivePhotos}
                                                                    >
                                                                        <Grid container>
                                                                            <Grid item>
                                                                                <FormControlLabel
                                                                                    sx={{ ml: '-6px !important' }}
                                                                                    control={
                                                                                        <Radio value={item.Id ?? 0} />
                                                                                    }
                                                                                    label={item.Id ?? 0}
                                                                                />
                                                                            </Grid>
                                                                        </Grid>
                                                                    </RadioGroup>
                                                                </FormControl>
                                                                <PhotoDetails
                                                                    item={item}
                                                                    key={item.Id}
                                                                    timezone={selectedPc?.Timezone}
                                                                    onPreviewChange={onPreviewChange}
                                                                    onSetSelectedItem={onSetSelectedPhotos}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    </Grow>
                                                </Grid>
                                            </Grow>
                                        ))
                                    ) : (
                                        <Grid item xs={12}>
                                            <NoDataCameras
                                                sx={{
                                                    borderRadius: '0px 0px 4px 4px',
                                                    height: '250px',
                                                    bgcolor: color.white,
                                                    border: 'none',
                                                }}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            </ScrollableBoxCustom>
                        ) : (
                            <Box>
                                <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                                    <BaseTextField
                                        placeholder={'E.g., ABC**, A**12'}
                                        inputRef={searchPlateRef}
                                        value={inputSearchValue}
                                        onChange={(e) => {
                                            setInputSearchValue(e.target.value);
                                            debouncedSetSearchValue(e.target.value);
                                        }}
                                        maxLength={10}
                                        InputProps={{
                                            endAdornment: (
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <FiSearch
                                                        color="disabled"
                                                        size={16}
                                                        style={{
                                                            color: 'rgba(156, 154, 154, 0.38)',
                                                            cursor: 'pointer',
                                                        }}
                                                    />
                                                    <Tooltip
                                                        title="Use * as a wildcard. Example: 'ABC**' or 'A**12'"
                                                        placement="top"
                                                        arrow
                                                    >
                                                        <span style={{ height: '14px' }}>
                                                            <FiInfo size={14} style={{ cursor: 'pointer' }} />
                                                        </span>
                                                    </Tooltip>
                                                </Stack>
                                            ),
                                        }}
                                    />
                                    <TimePicker
                                        label="From"
                                        value={fromTime}
                                        ampm={false}
                                        onChange={setFromTime}
                                        sx={{ width: 250, '& .MuiSvgIcon-root': { width: 18, height: 18 } }}
                                    />
                                    <TimePicker
                                        label="To"
                                        value={toTime}
                                        ampm={false}
                                        onChange={setToTime}
                                        sx={{ width: 250, '& .MuiSvgIcon-root': { width: 18, height: 18 } }}
                                    />
                                </Stack>
                                <ScrollableBoxCustom>
                                    <Box
                                        ref={searchBoxRef}
                                        style={{ maxHeight: 450, minHeight: 250, overflowY: 'auto' }}
                                    >
                                        {searchLoading && searchList.length === 0 ? (
                                            <Stack alignItems="center" justifyContent="center" minHeight={200}>
                                                <Grow in={true} timeout={Math.min(500, 1000)}>
                                                    <Box height={'100%'}>
                                                        <CircularProgress />
                                                    </Box>
                                                </Grow>
                                            </Stack>
                                        ) : searchList.length === 0 ? (
                                            <NoDataCameras
                                                sx={{ minHeight: 240, height: '100%', width: '100%' }}
                                                changeText="No data found"
                                            />
                                        ) : (
                                            <Grid container spacing={2}>
                                                {searchList.map((item, index) => (
                                                    <Grid item md={3} sm={6} xs={12} key={item.Id}>
                                                        <Box height={'100%'}>
                                                            <Box
                                                                key={item.Id}
                                                                bgcolor={color.white}
                                                                p={1}
                                                                border={1}
                                                                borderColor={color.grey200}
                                                                borderRadius={1}
                                                                position={'relative'}
                                                            >
                                                                <FormControl>
                                                                    <RadioGroup
                                                                        row
                                                                        aria-labelledby="radio-buttons-group-label"
                                                                        name="radio-buttons-group"
                                                                        value={selectedItemsLivePhotos?.Id || ''}
                                                                        onChange={handleRadioChangeItemsLivePhotos}
                                                                    >
                                                                        <Grid container>
                                                                            <Grid item>
                                                                                <FormControlLabel
                                                                                    sx={{ ml: '-6px !important' }}
                                                                                    control={
                                                                                        <Radio value={item.Id ?? 0} />
                                                                                    }
                                                                                    label={item.Id ?? 0}
                                                                                />
                                                                            </Grid>
                                                                        </Grid>
                                                                    </RadioGroup>
                                                                </FormControl>
                                                                <PhotoDetails
                                                                    item={item}
                                                                    key={item.Id}
                                                                    timezone={selectedPc?.Timezone}
                                                                    onPreviewChange={onPreviewChange}
                                                                    onSetSelectedItem={onSetSelectedPhotos}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        )}
                                        {searchLoading && searchList.length > 0 && (
                                            <Stack alignItems="center" justifyContent="center" py={2}>
                                                <Typography>Loading more...</Typography>
                                            </Stack>
                                        )}
                                    </Box>
                                </ScrollableBoxCustom>
                            </Box>
                        )}
                    </Stack>
                </Box>
            }
            customActions={
                <Stack direction={'column'} spacing={1} alignItems={'start'} width={'100%'}>
                    {selectedPlate && (
                        <Fade in={true} timeout={700}>
                            <Typography variant="body1" color={color.danger}>
                                Note: Click 'Confirm' to replace the photo and update the license plate for this group.
                                You can still edit the plate before confirming
                            </Typography>
                        </Fade>
                    )}
                    <Stack direction={'row'} width="100%" justifyContent="space-between">
                        <Button
                            sx={{ minWidth: 130 }}
                            variant="cancel"
                            onClick={() => {
                                setSelectedItemsLivePhotos(null);
                                setTab('overview');
                                onClose?.();
                                if (searchPlateRef.current) {
                                    searchPlateRef.current.value = '';
                                }
                                setInputSearchValue('');
                                setSearchValue('');
                                setFromTime(null);
                                setToTime(null);
                                setSearchList([]);
                                setSearchPage(1);
                                setSearchHasMore(true);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            sx={{ minWidth: 130 }}
                            variant="contained"
                            disabled={!selectedItemsLivePhotos}
                            onClick={() => {
                                handleChangePhotoFullLicense();
                                setSelectedItemsLivePhotos(null);
                            }}
                        >
                            Confirm
                        </Button>
                    </Stack>
                </Stack>
            }
        />
    );
}
